import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Clock, AlertTriangle, CalendarClock, Ban, Eye } from "lucide-react";
import clsx from "clsx";
import { useStore } from "../../stores/store";

export function ProactiveBanner() {
  const daily = useStore((s) => s.daily);
  const dismissed = useStore((s) => s.dailyDismissed);
  const dismiss = useStore((s) => s.dismissDaily);
  const setActiveCard = useStore((s) => s.setActiveCard);
  const setDeadlineFilter = useStore((s) => s.setDeadlineFilter);

  if (!daily || dismissed) return null;

  const hasAnything =
    daily.stale.length > 0 ||
    daily.overload ||
    daily.deadlineCreep.length > 0 ||
    daily.blocked.length > 0;

  if (!hasAnything) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="relative overflow-hidden border-b border-white/5"
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(600px_120px_at_15%_50%,rgba(168,85,247,0.18),transparent),radial-gradient(600px_120px_at_85%_50%,rgba(56,189,248,0.1),transparent)]" />

        <div className="relative flex items-center gap-3 px-5 py-2.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500/40 to-fuchsia-500/40 ring-1 ring-violet-400/30 shadow-[0_0_16px_rgba(168,85,247,0.4)]">
            <Sparkles className="h-3.5 w-3.5 text-violet-100" />
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            {daily.stale.length > 0 && (
              <Chip icon={<Clock className="h-3 w-3" />} tone="amber" title="Cards há 3+ dias em progresso">
                {daily.stale.length} stale em progresso
              </Chip>
            )}

            {daily.overload && (
              <Chip icon={<AlertTriangle className="h-3 w-3" />} tone="rose" title="WIP limit estourado">
                {daily.overload.laneTitle}: {daily.overload.count}/{daily.overload.limit}
              </Chip>
            )}

            {daily.deadlineCreep.length > 0 && (
              <Chip
                icon={<CalendarClock className="h-3 w-3" />}
                tone="rose"
                title="Cards com deadline passada ainda ativos"
                onClick={() => setDeadlineFilter("overdue")}
              >
                {daily.deadlineCreep.length} atrasad{daily.deadlineCreep.length === 1 ? "o" : "os"}
              </Chip>
            )}

            {daily.blocked.length > 0 && (
              <Chip icon={<Ban className="h-3 w-3" />} tone="rose" title="Cards na raia Bloqueado">
                {daily.blocked.length} bloqueado{daily.blocked.length === 1 ? "" : "s"}
              </Chip>
            )}
          </div>

          <button
            onClick={dismiss}
            className="ml-auto flex items-center gap-1 rounded p-1 text-[10px] text-slate-400 transition hover:bg-white/5 hover:text-slate-100"
            title="Fechar por hoje"
          >
            <Eye className="h-3 w-3" />
            <span className="hidden sm:inline">ok, fechar</span>
            <X className="h-3 w-3" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function Chip({
  children,
  icon,
  tone,
  title,
  onClick,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  tone: "amber" | "rose" | "violet";
  title?: string;
  onClick?: () => void;
}) {
  const toneCls = {
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20",
    rose: "border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20",
    violet: "border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20",
  }[tone];
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      title={title}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] transition",
        toneCls,
        !onClick && "cursor-default",
      )}
    >
      {icon}
      {children}
    </button>
  );
}
